export function slugify(text: string | undefined): string {
    if (!text) return "";
    return text.toLowerCase()
        .replace(/_/g, "-")
        .replace(/[\s\/]+/g, "-")
        .replace(/[^\w-]+/g, "");
}

/**
 * Extracts the string that feeds path generation from a filter_fields.location
 * value, which can be either a legacy string slug or the DR-408 map shape
 * ({ ref, name, latlng, range }). Returns undefined for anything else.
 * The ref ID (the curated location slug) is canonical for URLs: unlike the
 * editable display name, it never drifts or changes after creation.
 */
export function getLocationPathInput(location: unknown): string | undefined {
    if (!location) return undefined;
    if (typeof location === "string") return location;
    if (typeof location === "object") {
        const refId = (location as any).ref?.id;
        if (typeof refId === "string" && refId) return refId;
        const name = (location as any).name;
        if (typeof name === "string" && name) return name;
    }
    return undefined;
}

export function generatePath(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined,
    location: string | undefined
): string {
    const parts: string[] = [];

    // Guard: if nothing is provided, return empty string
    if (!commitment && !jobFunction && !industry && !location) {
        return "";
    }

    if (commitment) {
        parts.push(slugify(commitment));
    } else {
        parts.push("professionals");
    }

    if (jobFunction) {
        parts.push(slugify(jobFunction));
    }

    if (industry) {
        parts.push(slugify(industry));
    }

    if (location) {
        parts.push(slugify(location));
    }

    return parts.join("/");
}

/**
 * Extracts the human-readable location name from a filter_fields.location
 * value for use in generated copy ("near Austin, TX"). Only the DR-408 map
 * shape carries a display name; legacy string slugs produce undefined.
 */
export function getLocationDisplayName(location: unknown): string | undefined {
    if (location && typeof location === "object" && typeof (location as any).name === "string" && (location as any).name) {
        return (location as any).name;
    }
    return undefined;
}

/**
 * DR-439: location-based copy reads "in <Location>", except when an industry
 * is present — "in the X Industry in Philadelphia, PA" would double the "in",
 * so those combinations keep "near <Location>".
 */
function locationJoiner(industry: string | undefined): string {
    return industry ? "near" : "in";
}

export function generateTitle(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined,
    locationName?: string
): string {
    const base = generateBaseTitle(commitment, jobFunction, industry);
    if (!locationName) return base;
    if (!base) return `Find Experienced Professionals in ${locationName}`;
    return `${base} ${locationJoiner(industry)} ${locationName}`;
}

function generateBaseTitle(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined
): string {
    const pluralC = commitment ? pluralizeCommitment(commitment) : undefined;

    // 1. All three present
    if (pluralC && jobFunction && industry) {
        return `Find Experienced ${jobFunction} ${pluralC} in the ${industry} Industry`;
    }
    // 2. jobFunction and industry (no commitment)
    if (jobFunction && industry && !pluralC) {
        return `Find Experienced ${jobFunction} Professionals in the ${industry} Industry`;
    }
    // 3. commitment and industry (no jobFunction)
    if (pluralC && industry && !jobFunction) {
        return `Find Experienced ${pluralC} in the ${industry} Industry`;
    }
    // 4. commitment and jobFunction (no industry)
    if (pluralC && jobFunction && !industry) {
        return `Find Experienced ${jobFunction} ${pluralC}`;
    }
    // 5. Just commitment
    if (pluralC && !jobFunction && !industry) {
        return `Find Experienced ${pluralC}`;
    }
    // 6. Just jobFunction
    if (jobFunction && !pluralC && !industry) {
        return `Find Experienced ${jobFunction} Professionals`;
    }
    // 7. Just industry
    if (industry && !pluralC && !jobFunction) {
        return `Find Experienced Professionals in the ${industry} Industry`;
    }

    return "";
}

export function pluralizeCommitment(commitment: string | undefined, _depth = 0): string {
    if (!commitment) return "professionals";
    if (_depth > 3) return `${commitment.trim()}s`; // safety valve
    
    const clean = commitment.trim();
    
    const parenMatch = clean.match(/^(.+?)\s*(\([^)]+\))$/);
    if (parenMatch) {
        const mainPart = parenMatch[1].trim();
        const parenthetical = parenMatch[2];
        return `${pluralizeCommitment(mainPart, _depth + 1)} ${parenthetical}`;
    }

    const lower = clean.toLowerCase();
    
    if (lower === "full time" || lower === "full-time" || lower === "full_time") {
        return "Full-timers";
    }
    if (lower === "part time" || lower === "part-time" || lower === "part_time") {
        return "Part-timers";
    }
    
    if (clean.endsWith("s") || clean.endsWith("S")) {
        return clean;
    }
    
    if (lower.endsWith("ch") || lower.endsWith("sh") || lower.endsWith("x") || lower.endsWith("z")) {
        return `${clean}es`;
    }
    
    return `${clean}s`;
}

const REFERENCES_SENTENCE_FULL =
    "Discover verified video resumes, professional portfolios, and authentic references designed to provide deeper context into expertise, experience, and professional impact.";
// DR-436: meta descriptions drop the trailing clause to meet length best practices
const REFERENCES_SENTENCE_SHORT =
    "Discover verified video resumes, professional portfolios, and authentic references.";

function generateDescriptionSubject(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined,
    locationName?: string
): string | undefined {
    const pluralCommitment = commitment ? pluralizeCommitment(commitment) : "professionals";
    // DR-439: location-based pages carry the location in the copy as well
    const locationSuffix = locationName ? ` ${locationJoiner(industry)} ${locationName}` : "";

    if (jobFunction && industry) {
        return `Explore our directory of Experienced ${jobFunction} ${pluralCommitment} in ${industry} Industry${locationSuffix}`;
    }
    if (jobFunction) {
        return `Explore our directory of Experienced ${jobFunction} ${pluralCommitment}${locationSuffix}`;
    }
    if (industry) {
        return `Explore our directory of Experienced ${pluralCommitment} in the ${industry} Industry${locationSuffix}`;
    }
    if (commitment) {
        return `Explore our directory of Experienced ${pluralCommitment}${locationSuffix}`;
    }
    if (locationName) {
        return `Explore our directory of Experienced professionals${locationSuffix}`;
    }
    return undefined;
}

export function generateDescription(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined,
    locationName?: string
): string {
    const subject = generateDescriptionSubject(commitment, jobFunction, industry, locationName);
    return subject ? `${subject}. ${REFERENCES_SENTENCE_FULL}` : "";
}

export function generateMetaDescription(
    commitment: string | undefined,
    jobFunction: string | undefined,
    industry: string | undefined,
    locationName?: string
): string {
    const subject = generateDescriptionSubject(commitment, jobFunction, industry, locationName);
    return subject ? `${subject}. ${REFERENCES_SENTENCE_SHORT}` : "";
}
